import styled from '@emotion/styled';
import Icon from '@mdi/react';
import { Link } from 'react-router-dom';
import { BaseButton } from '../base/button';
import { Variables } from '../base/variables';

export const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 24px 24px 0px;
    @media (max-width: 768px) {
        padding: 16px 16px 0px;
    }

    @media (max-width: 480px) {
        padding: 12px 12px 0px;
    }
`;

export const Header = styled.div`
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        margin-bottom: 16px;
    }
`;

export const Title = styled.h1`
    font-size: 24px;
    font-weight: 600;
    color: ${Variables.Colors.n100};
    margin: 0;

    @media (max-width: 768px) {
        font-size: 20px;
    }

    @media (max-width: 480px) {
        font-size: 18px;
    }
`;

export const Layout = styled.div`
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 24px;
    align-items: start;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

export const NavRail = styled.nav`
    position: sticky;
    top: 16px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 16px 12px;
    max-height: calc(100vh - 120px);
    overflow: auto;

    @media (max-width: 1024px) {
        position: static;
        flex-direction: row;
        flex-wrap: wrap;
        gap: 6px;
        max-height: none;
    }
`;

export const ClaimLinkBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 0 10px 16px;

    @media (max-width: 1024px) {
        width: 100%;
        padding-bottom: 8px;
    }
`;

export const ClaimLinkLabel = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: ${Variables.ThemeLight.TextMild};
`;

export const ClaimDetailsLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: fit-content;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.3;
    color: ${Variables.ThemeLight.PrimaryConstant6};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

export const ClaimDetailsLinkIcon = styled(Icon)`
    flex-shrink: 0;
    color: inherit;
`;

export const NavTitle = styled.div`
    font-size: 12px;
    font-weight: 500;
    color: ${Variables.ThemeLight.TextMild};
    letter-spacing: 0.04em;
    padding: 0 10px 8px;

    @media (max-width: 1024px) {
        width: 100%;
        padding-bottom: 4px;
    }
`;

export const NavItem = styled(BaseButton)<{ $active?: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    text-align: left;
    border: none;
    border-radius: 8px;
    padding: 8px 10px;
    cursor: pointer;
    background: ${props => (props.$active ? Variables.ThemeLight.PrimaryOpacity2 : 'transparent')};
    color: ${props => (props.$active ? Variables.Colors.b300 : Variables.Colors.n70)};
    font-size: 14px;
    font-weight: 500;
    transition: background 120ms;

    &:hover {
        background: ${props => (props.$active ? Variables.Colors.b300_l96 : Variables.Colors.n15_24)};
    }

    @media (max-width: 1024px) {
        width: auto;
    }
`;

export const NavIcon = styled.span`
    display: inline-flex;
    flex: none;
    color: inherit;
`;

export const NavItemLabel = styled.span`
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const NavBadge = styled.span`
    flex: none;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: ${Variables.Colors.warning};
    color: ${Variables.Colors.white};
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
    text-align: center;
`;

export const NavDone = styled.span`
    flex: none;
    display: inline-flex;
    color: ${Variables.Colors.successful};
`;

export const MainColumn = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;

    @media (max-width: 768px) {
        gap: 16px;
    }

    @media (max-width: 480px) {
        gap: 12px;
    }
`;

export const Section = styled.div`
    padding: 24px;
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.nx8};
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    scroll-margin-top: 24px;

    @media (max-width: 768px) {
        padding: 16px;
        border-radius: 6px;
    }

    @media (max-width: 480px) {
        padding: 12px;
        border-radius: 4px;
    }
`;

export const SectionHeaderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
    flex-wrap: wrap;
`;

export const SectionTitle = styled.h2`
    font-size: 18px;
    font-weight: 500;
    color: ${Variables.ThemeLight.TextMedium};
    margin: 0;

    @media (max-width: 768px) {
        font-size: 16px;
    }

    @media (max-width: 480px) {
        font-size: 14px;
    }
`;

export const SectionPlaceholder = styled.p`
    font-size: 14px;
    color: ${Variables.Colors.n50};
    margin: 0;
    font-style: italic;

    @media (max-width: 480px) {
        font-size: 12px;
    }
`;

export const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 100px 0;

    @media (max-width: 768px) {
        padding: 60px 0;
    }

    @media (max-width: 480px) {
        padding: 40px 0;
    }
`;

export const StatusPill = styled.span<{ $tone: 'error' | 'success' }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 600;
    padding: 5px 9px;
    border-radius: 999px;
    border: 1px solid
        ${props =>
            props.$tone === 'error' ? Variables.ThemeLight.WarningMild1 : Variables.ThemeLight.SuccessMild1};
    background: ${props =>
        props.$tone === 'error' ? Variables.ThemeLight.WarningMild3 : Variables.ThemeLight.SuccessMild3};
    color: ${props =>
        props.$tone === 'error' ? Variables.ThemeLight.WarningInverted3 : Variables.ThemeLight.SuccessInverted3};
`;

export const ErrorSummaryBanner = styled.div`
    background: ${Variables.ThemeLight.WarningOpacity3};
    color: ${Variables.ThemeLight.WarningInverted5};
    border: 1px solid ${Variables.ThemeLight.WarningMild1};
    border-radius: ${Variables.Rounded.large};
    padding: 16px 18px;
    margin-bottom: 4px;
`;

export const ErrorSummaryHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

export const ErrorSummaryTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    flex: 1;
`;

export const ErrorSummaryDismiss = styled(BaseButton)`
    && {
        width: 28px;
        height: 28px;
        min-width: 0;
        padding: 0;
        border: none;
        background: transparent;
        border-radius: ${Variables.Rounded.medium};
        color: ${Variables.ThemeLight.TextMild};
        font-size: 20px;
        line-height: 1;
    }

    &&:hover {
        background: ${Variables.ThemeLight.NeutralOpacity2};
    }
`;

export const ErrorSummaryHint = styled.div`
    font-size: 13px;
    color: ${Variables.ThemeLight.TextMedium};
    margin-top: 4px;
`;

export const ErrorSummaryGroups = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
`;

export const ErrorSummaryGroup = styled.div``;

export const ErrorSummaryGroupTitle = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${Variables.ThemeLight.WarningConstant6};
    margin-bottom: 6px;
`;

export const ErrorSummarySourceTitle = styled.div`
    font-size: 13px;
    font-weight: 700;
    color: ${Variables.ThemeLight.TextStrong};
    margin-bottom: 2px;
`;

export const ErrorSummaryLink = styled(BaseButton)`
    && {
        display: flex;
        width: 100%;
        justify-content: flex-start;
        text-align: left;
        border: none;
        background: transparent;
        padding: 4px 0;
        min-height: 0;
        color: ${Variables.ThemeLight.TextStrong};
        font-size: 14px;
        font-weight: ${Variables.FontWeight.regular};
        align-items: baseline;
        flex-wrap: wrap;
        gap: 4px;
    }

    &&:hover {
        color: ${Variables.ThemeLight.PrimaryConstant6};
        text-decoration: underline;
        background: transparent;
    }
`;

export const ErrorSummaryPlace = styled.span`
    font-weight: ${Variables.FontWeight.semibold};
    color: ${Variables.ThemeLight.TextStrong};
`;

export const ErrorSummaryRest = styled.span`
    font-weight: ${Variables.FontWeight.regular};
    color: ${Variables.ThemeLight.TextMedium};
`;

export const ErrorSummaryPlain = styled.div`
    padding: 4px 0;
    color: ${Variables.ThemeLight.TextStrong};
    font-size: 14px;
`;

export const ErrorSummaryToggle = styled(BaseButton)`
    margin-top: 10px;
    padding: 0;
    min-height: 0;
    font-size: 13px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextMedium};
    text-decoration: underline;
`;

export const SuccessPanel = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
    padding: 48px 24px;
    background: ${Variables.Colors.white};
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

export const SuccessIcon = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #eef8f1;
    color: #399e7c;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    font-weight: 700;
`;

export const SuccessTitle = styled.h2`
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: ${Variables.Colors.n100};
`;

export const SuccessSubtitle = styled.p`
    margin: 0;
    font-size: 15px;
    color: ${Variables.Colors.n70};
    max-width: 420px;
`;

export const SuccessActions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 12px;
    flex-wrap: wrap;
    justify-content: center;
`;

export const SuccessPrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 500;
    border: 1px solid transparent;
    border-radius: 7px;
    padding: 0 18px;
    height: 40px;
    cursor: pointer;
    background: ${Variables.Colors.b300};
    color: ${Variables.Colors.white};
`;

export const SuccessSecondaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 500;
    border: 1px solid ${Variables.Colors.nx8};
    border-radius: 7px;
    padding: 0 16px;
    height: 40px;
    cursor: pointer;
    background: ${Variables.Colors.white};
    color: ${Variables.Colors.n100};
`;

export const FooterStatus = styled.div<{ $hasErrors?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${props =>
        props.$hasErrors ? Variables.ThemeLight.WarningConstant6 : Variables.ThemeLight.TextMedium};
    margin-bottom: 12px;
`;

export const StickyFooter = styled.div`
    position: sticky;
    bottom: 0;
    z-index: 35;
    background: ${Variables.ThemeLight.Background3};
    border-top: 1px solid ${Variables.ThemeLight.Border1};
    box-shadow: 0 -2px 8px rgba(21, 29, 43, 0.05);
    padding: 11px 24px;
    margin: 16px -24px 0;
    display: flex;
    align-items: center;
    gap: 14px;

    @media (max-width: 768px) {
        margin: 12px -16px 0;
        padding: 11px 16px;
        flex-wrap: wrap;
    }

    @media (max-width: 480px) {
        margin: 12px -12px 0;
        padding: 11px 12px;
    }
`;

export const StickyFooterLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    flex: 1;
    min-width: 0;
    flex-wrap: wrap;
`;

export const StickyFooterTitle = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 18px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
    white-space: nowrap;
`;

export const StickyFooterActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: none;
    margin-left: auto;
`;

export const StickyFooterStatus = styled.span<{ $hasErrors?: boolean; $ok?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${props =>
        props.$hasErrors
            ? Variables.ThemeLight.WarningInverted4
            : props.$ok
              ? Variables.ThemeLight.SuccessInverted3
              : Variables.ThemeLight.TextMedium};
`;

export const JumpNextButton = styled(BaseButton)`
    && {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 13px;
        font-weight: 500;
        border: 1px solid ${Variables.ThemeLight.WarningOpacity6};
        border-radius: ${Variables.Rounded.medium};
        padding: 0 13px;
        height: 34px;
        min-width: 0;
        background: ${Variables.ThemeLight.Background3};
        color: ${Variables.ThemeLight.WarningInverted4};
    }

    &&:hover {
        background: ${Variables.ThemeLight.WarningMild3};
    }
`;

export const StickyFooterCancelButton = styled(BaseButton)`
    && {
        height: 38px;
        min-width: 80px;
        padding: 0 16px;
        border-radius: ${Variables.Rounded.medium};
        border: 1px solid ${Variables.ThemeLight.Border1};
        background: ${Variables.ThemeLight.Background3};
        color: ${Variables.ThemeLight.TextStrong};
        font-size: 14px;
        font-weight: 500;
    }

    &&:hover {
        background: ${Variables.ThemeLight.Background1};
    }
`;

export const StickyFooterSubmitButton = styled(BaseButton)`
    && {
        height: 38px;
        min-width: 124px;
        padding: 0 18px;
        border-radius: ${Variables.Rounded.medium};
        border: 1px solid transparent;
        background: ${Variables.ThemeLight.PrimaryConstant6};
        color: ${Variables.ThemeLight.Background3};
        font-size: 14px;
        font-weight: 600;
    }

    &&:hover {
        background: ${Variables.ThemeLight.PrimaryInverted3};
    }

    &&:disabled {
        background: ${Variables.ThemeLight.NeutralOpacity3};
        color: ${Variables.ThemeLight.TextXMild};
    }
`;
